/**
 * 시공사례. 상담 때 「저희가 이런 걸 했습니다」 를 보여 주는 갤러리 한 장이다.
 *
 * 이미지는 URL 만 갖는다 — 업로드·리사이즈는 저장소 밖(Storage)의 일이고,
 * 지금은 목 URL 로 화면을 먼저 세운다.
 */

import { AreaBand, Residence } from "../customer/types";
import { OrgScopedRow } from "../common/types";

export interface PortfolioImage {
  url: string;
  caption: string;
  // 현장 사진이면 그 행 id — url 은 서버가 세션 전용 경로로 다시 쓴다(`0028`).
  photo_id?: string;
  // 사진 태그 — 전/후와 조직 공간 코드(`codes.space`).
  stage?: PortfolioImageStage;
  space?: string;
}

export const PORTFOLIO_IMAGE_STAGES = ["BEFORE", "AFTER"] as const;
export type PortfolioImageStage = (typeof PORTFOLIO_IMAGE_STAGES)[number];

// 공개 페이지에 보일 항목 — SQL check(`0046`)와 같은 목록이다.
export const PORTFOLIO_PUBLIC_FIELDS = ["amount", "period", "area", "designer", "timeline", "review"] as const;
export type PortfolioPublicField = (typeof PORTFOLIO_PUBLIC_FIELDS)[number];

// 기본은 기간·면적만 켠다(금액·디자이너는 끔, 오너 결정 D6).
export const PORTFOLIO_PUBLIC_FIELDS_DEFAULT: readonly PortfolioPublicField[] = ["period", "area"];

export const PORTFOLIO_PUBLIC_FIELD_LABEL: Record<PortfolioPublicField, string> = {
  amount: "금액(구간)",
  period: "공사 기간",
  area: "면적",
  designer: "담당 디자이너",
  timeline: "공정 챕터",
  review: "고객 후기",
};

// 공정 한 챕터 — 공정명·날짜·일지 발췌·현장 사진 id. 고객 이름·번지는 싣지 않는다.
export interface PortfolioTimelineEntry {
  name: string;
  start: string | null;
  end: string | null;
  excerpts: string[];
  photo_ids: string[];
}

// 챕터 발췌 상한 — 초안 생성과 편집이 같은 값을 쓴다.
export const PORTFOLIO_EXCERPT_BODY_MAX = 120;
export const PORTFOLIO_EXCERPTS_MAX = 5;
// 한 줄 = 「MM-DD 」 6자 + 「추가공정 · 」 7자 + 본문.
export const PORTFOLIO_EXCERPT_LINE_MAX = 6 + 7 + PORTFOLIO_EXCERPT_BODY_MAX;

export interface Portfolio extends OrgScopedRow {
  title: string;
  summary: string;
  // 본문 — 현장에서 만든 초안의 기간·공정·일지 발췌(`0028`).
  body: string;
  // 초안을 만든 현장. 한 현장에 초안 하나(`portfolio_org_project_key`).
  project_id: string | null;
  area_band: AreaBand | null;
  residence: Residence | null;
  /** 목록 카드에 쓰는 대표 이미지. */
  cover_url: string;
  images: PortfolioImage[];
  tags: string[];
  /** false 면 내부에서만 보인다. 고객에게 주는 화면은 true 만 싣는다. */
  published: boolean;
  /** 손으로 정한 노출 순서. 작을수록 앞. `reorder` 만 바꾼다. */
  sort_order: number;
  // 담당 디자이너(같은 조직 멤버) — 초안은 현장 담당자(`0046`).
  designer_id: string | null;
  // 당시 계약 총액(원) — 공개해도 구간으로만 보인다(`amount-band.ts`).
  amount_won: number | null;
  public_fields: PortfolioPublicField[];
  // 초안이 현장에서 만든 공정 챕터 — 편집은 발췌 줄만 고친다.
  timeline: PortfolioTimelineEntry[];
}

/** 등록·편집이 건드리는 열만. id·org_id·sort_order 는 저장소가 정한다. */
export type PortfolioInput = Omit<
  Portfolio,
  keyof OrgScopedRow | "sort_order" | "project_id"
>;

export type PortfolioImageInput = { url: string; caption?: string; photo_id?: string; stage?: PortfolioImageStage; space?: string };

/** 화면에서 넘어오는 모양 — caption·tags 는 없어도 된다. 공개 데이터 칸은 안 보내면 그대로 둔다. */
export type PortfolioCreateInput = Omit<
  PortfolioInput,
  "images" | "tags" | "summary" | "body" | "designer_id" | "amount_won" | "public_fields" | "timeline"
> & {
  summary?: string;
  body?: string;
  images: PortfolioImageInput[];
  tags?: string[];
  designer_id?: string | null;
  amount_won?: number | null;
  public_fields?: PortfolioPublicField[];
};

// timeline_excerpts — 챕터별 발췌 줄만. 이름·날짜·photo_ids 는 저장된 값을 지킨다.
export type PortfolioPatch = Partial<PortfolioCreateInput> & { timeline_excerpts?: string[][] };

// 현장에서 만든 초안 — id·기록은 앱이 만들어 보내고 저장소는 쓰기만 한다.
export interface PortfolioFromProjectInput {
  portfolio: PortfolioInput & { id: string; project_id: string };
  activities: Array<{
    actor_id: string | null;
    action: string;
    target_type: "portfolio" | "project";
    target_id: string;
    summary: string;
    meta?: Record<string, string>;
  }>;
}

export interface PortfolioFromProjectResult {
  portfolio: Portfolio;
  // false 면 이미 있던 초안을 돌려준 것이다(한 현장에 하나).
  created: boolean;
}

// 로그인 없는 공개 페이지가 읽는 열 — project_id 는 사진 소속 검사에만, public_fields 는 끈 항목을 DTO 에서 빼는 데만 쓴다.
export type PublicPortfolioRow = Pick<
  Portfolio,
  | "id"
  | "org_id"
  | "project_id"
  | "title"
  | "summary"
  | "area_band"
  | "residence"
  | "cover_url"
  | "images"
  | "tags"
  | "published"
  | "sort_order"
  | "public_fields"
>;

// 상세 — 본문과, 공개 링크 B 가 공개 항목대로 거르는 공정 챕터(photo_ids 는 화면에 싣지 않는다).
export type PublicPortfolioDetailRow = PublicPortfolioRow & Pick<Portfolio, "body" | "timeline">;

// 공개 사례가 가리키는 현장 사진 행 — 소속(org·project)과 바이트 경로만.
export interface PublicPortfolioPhotoRow {
  id: string;
  org_id: string;
  project_id: string;
  storage_path: string;
  mime_type: string;
}
